(() => {
  const SELECTOR = '[data-pasquini-looks]';

  const enableProductListDrag = (root) => {
    root.querySelectorAll('.pasquini-looks__product-list').forEach((list) => {
      if (list.dataset.dragReady === 'true') return;

      const state = {
        active: false,
        dragged: false,
        startX: 0,
        startScrollLeft: 0,
      };

      list.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;

        state.active = true;
        state.dragged = false;
        state.startX = event.clientX;
        state.startScrollLeft = list.scrollLeft;
      });

      list.addEventListener('pointermove', (event) => {
        if (!state.active) return;
        // Botão solto fora da lista antes de virar arrasto (sem captura, o pointerup não chegou).
        if (event.buttons === 0) {
          state.active = false;
          return;
        }

        const distance = event.clientX - state.startX;
        if (Math.abs(distance) > 3) {
          // Captura só quando o arrasto começa: capturar no pointerdown redireciona
          // o click para a lista e o link do produto nunca navega.
          if (!list.hasPointerCapture(event.pointerId)) list.setPointerCapture(event.pointerId);
          state.dragged = true;
          list.classList.add('is-dragging');
          list.scrollLeft = state.startScrollLeft - distance;
          event.preventDefault();
        }
      });

      const finishDrag = (event) => {
        if (!state.active) return;
        state.active = false;
        list.classList.remove('is-dragging');
        if (list.hasPointerCapture(event.pointerId)) list.releasePointerCapture(event.pointerId);
      };

      // O drag nativo de link/imagem dispara pointercancel e interrompe a rolagem.
      list.addEventListener('dragstart', (event) => event.preventDefault());
      list.addEventListener('pointerup', finishDrag);
      list.addEventListener('pointercancel', finishDrag);
      list.addEventListener(
        'click',
        (event) => {
          if (!state.dragged) return;
          event.preventDefault();
          event.stopPropagation();
          state.dragged = false;
        },
        true
      );

      list.dataset.dragReady = 'true';
    });
  };

  /**
   * Desktop: only the first slide's video autoplays; hovering any other
   * slide plays its video and pauses whatever else is playing, reverting to
   * the first video on mouseleave.
   * Mobile: only the current (active) swiper slide's video plays; changing
   * slides pauses the previous one and plays the new active slide's video.
   */
  const setupVideoBehavior = (root, swiper) => {
    const slides = Array.from(root.querySelectorAll('.pasquini-looks__slide'));
    const videos = slides.map((slide) => slide.querySelector('video'));
    const firstVideoIndex = videos.findIndex(Boolean);
    if (firstVideoIndex === -1) return;

    const desktopQuery = window.matchMedia('(min-width: 1020px)');

    const playOnly = (index) => {
      videos.forEach((video, videoIndex) => {
        if (videoIndex === index) return;
        video?.pause();
      });
      videos[index]?.play().catch(() => {});
    };

    const applyForViewport = () => {
      if (desktopQuery.matches) {
        playOnly(firstVideoIndex);
      } else {
        playOnly(swiper?.activeIndex ?? 0);
      }
    };

    slides.forEach((slide, index) => {
      if (!videos[index]) return;

      slide.addEventListener('mouseenter', () => {
        if (!desktopQuery.matches) return;
        playOnly(index);
      });

      slide.addEventListener('mouseleave', () => {
        if (!desktopQuery.matches || index === firstVideoIndex) return;
        videos[index]?.pause();
        playOnly(firstVideoIndex);
      });
    });

    const syncMobileActiveSlide = () => {
      if (desktopQuery.matches || !swiper) return;
      playOnly(swiper.activeIndex);
    };

    swiper?.on('slideChange', syncMobileActiveSlide);
    desktopQuery.addEventListener('change', applyForViewport);
    root.pasquiniLooksVideoQuery = desktopQuery;
    root.pasquiniLooksVideoQueryHandler = applyForViewport;

    applyForViewport();
  };

  /**
   * Custom pagination bullets (same markup/behavior as the Pasquini
   * Depoimentos "quem compra, recomenda" carousel): the active bullet grows
   * into a pill, in sync with the swiper's current slide, and clicking a
   * bullet navigates to that slide.
   */
  const setupBullets = (root, swiper) => {
    const bullets = Array.from(root.querySelectorAll('[data-pasquini-looks-bullet]'));
    if (bullets.length === 0 || !swiper) return;

    const setActiveBullet = (index) => {
      bullets.forEach((bullet, bulletIndex) => {
        const isActive = bulletIndex === index;
        bullet.classList.toggle('is-active', isActive);
        bullet.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    bullets.forEach((bullet, index) => {
      bullet.addEventListener('click', () => swiper.slideTo(index));
    });

    swiper.on('slideChange', () => setActiveBullet(swiper.activeIndex));
    setActiveBullet(swiper.activeIndex);
  };

  const destroy = (root) => {
    root.pasquiniLooksSwiper?.destroy(true, true);
    delete root.pasquiniLooksSwiper;
    if (root.pasquiniLooksVideoQuery && root.pasquiniLooksVideoQueryHandler) {
      root.pasquiniLooksVideoQuery.removeEventListener('change', root.pasquiniLooksVideoQueryHandler);
    }
    delete root.pasquiniLooksVideoQuery;
    delete root.pasquiniLooksVideoQueryHandler;
    delete root.dataset.pasquiniLooksReady;
  };

  const initialize = (root) => {
    if (!root || root.dataset.pasquiniLooksReady === 'true' || typeof window.Swiper === 'undefined') return;

    enableProductListDrag(root);

    const carousel = root.querySelector('[data-pasquini-looks-carousel]');
    const slides = carousel?.querySelectorAll('.swiper-slide');
    if (!carousel || !slides?.length) return;

    const previous = root.querySelector('[data-pasquini-looks-previous]');
    const next = root.querySelector('[data-pasquini-looks-next]');

    root.pasquiniLooksSwiper = new window.Swiper(carousel, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 12,
      watchOverflow: true,
      noSwiping: true,
      noSwipingSelector: '.pasquini-looks__product-list',
      navigation: {
        prevEl: previous,
        nextEl: next,
      },
      breakpoints: {
        1020: {
          slidesPerView: 4,
          slidesPerGroup: 1,
          spaceBetween: 16,
        },
      },
    });

    setupVideoBehavior(root, root.pasquiniLooksSwiper);
    setupBullets(root, root.pasquiniLooksSwiper);

    root.dataset.pasquiniLooksReady = 'true';
  };

  const initializeAll = () => {
    document.querySelectorAll(SELECTOR).forEach(initialize);
  };

  const waitForSwiper = (attempt = 0) => {
    if (typeof window.Swiper !== 'undefined') {
      initializeAll();
      return;
    }

    if (attempt >= 40) return;
    window.setTimeout(() => waitForSwiper(attempt + 1), 50);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForSwiper());
  } else {
    waitForSwiper();
  }

  document.addEventListener('shopify:section:load', () => waitForSwiper());
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(SELECTOR) || event.target.closest?.(SELECTOR);
    if (root) destroy(root);
  });
})();
